import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InvoiceService } from 'src/app/services/core/quickbook/invoices/invoice.service';
import { InvoicePaymentService } from 'src/app/services/core/quickbook/invoice-payments/invoice-payment.service';
import { ProductServiceService } from 'src/app/services/core/quickbook/product-services/product-service.service';
import { sweetAlert } from 'src/app/utils/swal';
import { getAmountPaid, getInvoicePaymentTypeLabel, getRemainingBalance } from 'src/app/utils/common.utils';

@UntilDestroy()

@Component({
  selector: 'app-receive-payment',
  templateUrl: './receive-payment.component.html',
  styleUrls: ['./receive-payment.component.css']
})
export class ReceivePaymentComponent implements OnInit {
  payForm!: FormGroup;
  submitted = false;
  invoiceId: any;
  invoice: any;
  accounts: any;
  selectedAccount: any;
  invoicePayments: any[] = [];
  amountPaid = 0;
  remainingBalance = 0;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private invoiceService: InvoiceService,
    private invoicePaymentService: InvoicePaymentService,
    private productService: ProductServiceService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.payForm = this.fb.group({
      CustomerRef: this.fb.group({
        name: [''],
        value: ['']
      }),
      TotalAmt: [0, [Validators.required, Validators.min(0.01)]],
      DepositToAccountRef: this.fb.group({
        name: [''],
        value: ['']
      })
    });

    this.activatedRoute.params.pipe(untilDestroyed(this)).subscribe((params) => {
      this.invoiceId = params['id'];
      if (this.invoiceId) {
        this.getInvoiceById();
      }
    });
    this.getAccounts();
  }

  get form() {
    return this.payForm.controls;
  }

  getInvoiceById(withAccounts = false) {
    this.invoiceService.getInvoiceById(this.invoiceId).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        this.invoice = data;
        this.amountPaid = getAmountPaid(data);
        this.remainingBalance = getRemainingBalance(data);
        this.payForm.get('TotalAmt')?.setValidators([Validators.required, Validators.min(0.01), Validators.max(this.remainingBalance)]);
        this.payForm.patchValue({
          CustomerRef: {
            name: data.CustomerRef?.name || '',
            value: data.CustomerRef?.value || ''
          },
          TotalAmt: this.remainingBalance
        });
        this.getInvoicePayments();
        if (withAccounts) {
          this.getAccounts();
        }
      }
    });
  }

  getInvoicePayments() {
    this.invoicePaymentService.getInvoicePayments({
      invoice: this.invoiceId,
      limit: 9999,
      sortBy: 'createdAt:desc'
    }).pipe(untilDestroyed(this)).subscribe((data: any) => {
      this.invoicePayments = (data?.results || []).map((payment: any) => ({
        ...payment,
        typeLabel: getInvoicePaymentTypeLabel(payment)
      }));
    });
  }

  getAccounts() {
    this.productService.getAccount({ forPayment: 'true' }).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        this.accounts = Array.isArray(data) ? data : (data.results || []);
        if (this.selectedAccount) {
          this.selectedAccount = this.accounts.find((account: any) => account.Id === this.selectedAccount.Id) || this.selectedAccount;
        }
      }
    });
  }

  onAccountChange(event: Event) {
    const selectedId = (event.target as HTMLSelectElement).value;
    if (!selectedId) {
      this.selectedAccount = null;
      this.payForm.patchValue({ DepositToAccountRef: { name: '', value: '' } });
      return;
    }
    const selectedAccount = this.accounts?.find((account: any) => account.Id === selectedId);
    if (!selectedAccount) return;
    this.selectedAccount = selectedAccount;
    this.payForm.patchValue({
      DepositToAccountRef: {
        name: selectedAccount.Name,
        value: selectedAccount.Id
      }
    });
  }

  onSave() {
    this.submitted = true;
    if (this.payForm.invalid) return;
    if (!this.invoice?.Id) {
      sweetAlert('Error', 'Invoice must be synced to QuickBooks before receiving a payment', 'error', 'Ok');
      return;
    }

    const { DepositToAccountRef, ...rest } = this.payForm.value;
    const payload: any = {
      ...rest,
      invoice: this.invoiceId,
      TotalAmt: Number(this.payForm.value.TotalAmt)
    };
    if (DepositToAccountRef?.value) {
      payload.DepositToAccountRef = DepositToAccountRef;
    }

    this.invoicePaymentService.saveInvoicePayment(payload).pipe(untilDestroyed(this)).subscribe({
      next: (data: any) => {
        if (data) {
          this.router.navigateByUrl('/quickbook/invoices');
        }
      },
      error: (err) => {
        sweetAlert('Error', err, 'error', 'Ok');
      }
    });
  }

  canPushPayment(payment: any) {
    return !!(payment && !payment.Id && this.invoice?.Id);
  }

  canVoidPayment(payment: any) {
    return !!(payment?.Id && +payment.TotalAmt > 0);
  }

  async onVoidPayment(payment: any) {
    const result = await sweetAlert('Alert', 'Void this payment? The invoice balance will be restored.', 'warning', 'Yes', 'No');
    if (!result.value) return;
    this.invoicePaymentService.voidPayment(payment.id).pipe(untilDestroyed(this)).subscribe({
      next: () => this.getInvoiceById(),
      error: (err) => sweetAlert('Error', err, 'error', 'Ok')
    });
  }

  async onDeletePayment(payment: any) {
    const result = await sweetAlert('Alert', 'Delete this payment? The invoice balance will be restored.', 'warning', 'Yes', 'No');
    if (!result.value) return;
    this.invoicePaymentService.deletePayment(payment.id).pipe(untilDestroyed(this)).subscribe({
      next: () => this.getInvoiceById(),
      error: (err) => sweetAlert('Error', err, 'error', 'Ok')
    });
  }

  async onSyncPayment(payment: any) {
    if (!this.canPushPayment(payment)) return;
    const result = await sweetAlert(
      'Alert',
      'Are you sure you want to overwrite the QuickBooks data with payment data',
      'warning',
      'Yes',
      'No'
    );
    if (!result.value) return;

    this.invoicePaymentService.syncInvoicePayment(payment.id, { isSyncFrom: false }).pipe(untilDestroyed(this)).subscribe({
      next: (data: any) => {
        this.getInvoiceById();
        sweetAlert('Success', data?.message || 'Synced successfully', 'success', 'Ok');
      },
      error: (err) => sweetAlert('Error', err, 'error', 'Ok')
    });
  }
}
